import { FABRIC_CAPTURE_THRESHOLDS } from '../constants/thresholds';
import { distanceStateFromCoverage } from '../captureGuidance/distanceFromCoverage';
import { computeImageQualityScores } from './imageMetrics';

/**
 * @param {ImageData} imageData
 * @param {import('../fabricDetector/FabricDetector').FabricDetector} detector
 * @param {typeof FABRIC_CAPTURE_THRESHOLDS} [thresholds]
 * @returns {Promise<import('../types').ValidationResult>}
 */
export async function validateFabricCapture(imageData, detector, thresholds = FABRIC_CAPTURE_THRESHOLDS) {
  const fabricMetrics = await detector.detectFromImageData(imageData);
  const quality = computeImageQualityScores(imageData);

  /** @type {import('../types').ValidationIssue[]} */
  const issues = [];

  const dist = distanceStateFromCoverage(fabricMetrics.coverageRatio, thresholds);
  const tooClose = dist === 'too_close';
  const tooFar = dist === 'too_far';

  if (tooClose) {
    issues.push({
      code: 'too_close',
      message: 'Image too close. Move phone slightly farther.',
      severity: 'error',
    });
  } else if (tooFar) {
    issues.push({
      code: 'too_far',
      message: 'Fabric is too small in the frame. Move closer.',
      severity: 'error',
    });
  }

  if (quality.brightnessMean < thresholds.minBrightnessThreshold) {
    issues.push({
      code: 'low_light',
      message: 'Lighting is too low. Move to a brighter area.',
      severity: 'error',
    });
  }

  if (quality.sharpness < thresholds.minSharpnessThreshold) {
    issues.push({
      code: 'too_blurry',
      message: 'Image is blurry. Hold steady and retake.',
      severity: 'error',
    });
  }

  const m = thresholds.edgeCutoffMarginRatio;
  const { bboxX, bboxY, bboxW, bboxH, coverageRatio } = fabricMetrics;
  const touchesEdge =
    bboxX <= m ||
    bboxY <= m ||
    bboxX + bboxW >= 1 - m ||
    bboxY + bboxH >= 1 - m;
  /**
   * Edge cutoff: only when we think fabric is mid-sized but clipped.
   * Skip when “too close” (distance is the real issue) or fabric clearly fills most of the frame
   * (bbox naturally meets edges — not the same as “cropped swatch”).
   */
  const skipEdgeCutoff =
    tooClose ||
    tooFar ||
    coverageRatio >= thresholds.idealMinCoverage ||
    fabricMetrics.confidence < thresholds.minDetectorConfidenceForReady;

  if (touchesEdge && !skipEdgeCutoff) {
    issues.push({
      code: 'edge_cutoff',
      message: 'Fabric is not fully visible. Fit the full fabric in the frame.',
      severity: 'error',
    });
  }

  if (quality.centroidOffsetRatio > thresholds.maxCentroidOffsetRatio && fabricMetrics.confidence >= 0.4) {
    issues.push({
      code: 'off_center',
      message: 'Center the fabric in the frame and keep the phone parallel to it.',
      severity: 'warning',
    });
  }

  const hardIssues = issues.filter((i) => i.severity !== 'warning');
  const ok = hardIssues.length === 0;

  return {
    ok,
    issues,
    fabricMetrics,
    quality,
  };
}
